// src/api/posts.js

import { auth } from "../firebase";
import { apiFetchWithRetry } from "../lib/request";

// ✅ Single source of truth: Firebase Auth UID only
export function getUserId() {
  return auth.currentUser?.uid ?? null;
}

export async function fetchPostMeta() {
  const json = await apiFetchWithRetry('/posts/meta', { method: 'GET' });
  return json?.result || json;
}

/**
 * Attach hero image metadata to a post
 * POST /posts/:postId/image
 */
export async function attachPostImage(postId, payload) {
  if (!postId) throw new Error("Missing postId");

  return apiFetchWithRetry(`/posts/${postId}/image`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * GET /posts/account/:accountId
 */
export async function fetchPostsByAccount(accountId) {
  if (!accountId) throw new Error("Missing accountId (cannot fetch posts)");

  const json = await apiFetchWithRetry(`/posts/account/${accountId}`, { method: "GET" });

  return json?.result ?? json?.posts ?? json;
}

export async function deletePost(postId) {
  if (!postId) throw new Error("Missing post ID");
  await apiFetchWithRetry(`/posts/${encodeURIComponent(postId)}`, { method: "DELETE" });
}

/**
 * POST /posts  (create)
 */
/**
 * POST /posts (create)
 */
export async function createPost({
  accountId,
  title,
  postKind,
  kind = "article",
}) {
  if (!accountId) throw new Error("Missing accountId (cannot create post)");
  if (!title) throw new Error("Missing title");

  const json = await apiFetchWithRetry(`/posts`, {
    method: "POST",
    body: JSON.stringify({
      accountId,
      title: title.trim(),
      name: title.trim(),
      postKind,
      kind,
    }),
  });

  const p = json?.post || json?.result || json;
  const now = Date.now();

  return {
    id: p.id,
    accountId: p.accountId ?? accountId,
    title: p.title ?? p.name ?? title,
    name: p.name ?? title,
    postKind: p.postKind ?? postKind,
    kind: p.kind ?? kind,
    createdAt: p.createdAt ?? now,
    updatedAt: p.updatedAt ?? now,
  };
}


/**
 * POST /posts  (update/upsert)
 * (your API appears to upsert on POST /posts with an id)
 */
export async function updatePost(id, updates = {}) {
  if (!id) throw new Error("Missing post ID");

  const json = await apiFetchWithRetry(`/posts`, {
    method: "POST",
    body: JSON.stringify({
      id,
      postId: id, // keep if your backend expects it
      ...updates,
    }),
  });

  const p = json?.post || json?.result || json;
  const now = Date.now();

  return {
    id: p.id ?? id,
    ownerId: p.ownerId,
    title: p.title ?? p.name ?? updates.title,
    name: p.name ?? p.title ?? updates.name ?? updates.title,
    postKind: p.postKind ?? updates.postKind,
    kind: p.kind ?? updates.kind,
    createdAt: p.createdAt ?? now,
    updatedAt: p.updatedAt ?? now,
  };
}
